import React, { useState, useEffect, useCallback } from 'react';
import { UserProfile } from '../types';
import {
  User,
  Building2,
  Mail,
  Twitter,
  Shield,
  Calendar as CalendarIcon,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Save,
  Trash2,
  X,
  AlertTriangle,
  Loader2,
  Check,
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase, handleSupabaseError, OperationType } from '../supabase';
import { debounce } from 'lodash';

interface AccountSettingsProps {
  user: UserProfile;
  onBack: () => void;
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55 mb-1.5">
    {children}
  </p>
);

const inputBase =
  'w-full h-11 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm font-medium text-[#0b0b0c] placeholder:text-[#0b0b0c]/40 focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30';

export const AccountSettings: React.FC<AccountSettingsProps> = ({ user, onBack }) => {
  const [displayName, setDisplayName] = useState(user.displayName || '');
  const [companyName, setCompanyName] = useState(user.companyName || '');
  const [nicknameLoading, setNicknameLoading] = useState(false);
  const [nicknameStatus, setNicknameStatus] = useState<'available' | 'taken' | 'none'>('none');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const checkUniqueness = async (name: string) => {
    if (!name || name.length < 2) {
      setNicknameStatus('none');
      setSuggestions([]);
      return;
    }
    if (name.toLowerCase() === user.displayName.toLowerCase()) {
      setNicknameStatus('available');
      setSuggestions([]);
      return;
    }
    setNicknameLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('profiles')
        .select('id')
        .ilike('display_name', name)
        .neq('id', user.uid)
        .limit(1);
      if (err) throw err;
      const isTaken = !!data && data.length > 0;
      if (!isTaken) {
        setNicknameStatus('available');
        setSuggestions([]);
      } else {
        setNicknameStatus('taken');
        const bases = [
          `${name}${Math.floor(Math.random() * 99)}`,
          `${name}_${Math.floor(Math.random() * 99)}`,
          `${name}2026`,
          `${name}_v2`,
          `the_${name}`,
        ];
        setSuggestions(bases.sort(() => 0.5 - Math.random()).slice(0, 3));
      }
    } catch (err) {
      console.error('Error checking uniqueness:', err);
    } finally {
      setNicknameLoading(false);
    }
  };

  const debouncedCheck = useCallback(
    debounce((name: string) => checkUniqueness(name), 500),
    [user.displayName],
  );

  useEffect(() => {
    if (displayName) {
      setNicknameStatus('none');
      debouncedCheck(displayName);
    } else {
      setNicknameStatus('none');
    }
  }, [displayName, debouncedCheck]);

  const memberSince = user.createdAt ? format(user.createdAt, 'MMMM d, yyyy') : 'Recently';

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ display_name: displayName, company_name: companyName })
        .eq('id', user.uid);
      if (error) throw error;
      setStatus({ type: 'success', message: 'Profile updated successfully.' });
      setTimeout(() => setStatus(null), 4000);
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to update profile. Please try again.' });
      handleSupabaseError(error, OperationType.UPDATE, 'profiles');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') return;
    setDeleting(true);
    setDeleteError(null);
    try {
      // Anonymise events created by this user (set created_by = null via RLS-allowed self-update)
      const { error: eventsError } = await supabase
        .from('events')
        .update({ created_by: null })
        .eq('created_by', user.uid);
      if (eventsError) throw eventsError;

      // Delete the profile row (RLS allows owner)
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.uid);
      if (profileError) throw profileError;

      // Sign out — note: the auth.users row deletion requires a service-role
      // Edge Function (e.g. /admin-delete-self). We sign out here and the
      // backend will reap the orphaned auth row in a scheduled job.
      await supabase.auth.signOut();
      window.location.href = '/?deleted=true';
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError('Failed to delete account. Please contact support.');
      setDeleting(false);
    }
  };

  const trusted = user.tier === 'trusted';
  const dirty = displayName !== user.displayName || companyName !== user.companyName;

  return (
    <div className="max-w-4xl mx-auto pb-20 space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          aria-label="Back"
          className="w-10 h-10 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white inline-flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
            Settings
          </p>
          <h2 className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-[#0b0b0c]">
            Account
          </h2>
        </div>
      </div>

      <div className="sticker bg-white p-4 sm:p-6">
        <div className="flex items-end justify-between gap-3 mb-6">
          <div className="w-24 h-24 sticker-soft bg-[#f3ecfc] flex items-center justify-center">
            {user.authProvider === 'twitter' ? (
              <Twitter className="w-10 h-10 text-[#9945ff]" />
            ) : (
              <User className="w-10 h-10 text-[#0b0b0c]/55" />
            )}
          </div>
          <span
            className="px-3 py-1 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
            style={{ backgroundColor: trusted ? '#14f195' : '#ffd24a', color: '#0b0b0c' }}
          >
            {user.tier || 'Standard'} tier
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-5">
            <h3 className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
              Profile information
            </h3>

            <div>
              <FieldLabel>Nickname</FieldLabel>
              <div className="relative">
                <input
                  type="text"
                  className={`${inputBase} px-4 pr-10 ${
                    nicknameStatus === 'taken'
                      ? 'border-[#ff6a6a]'
                      : nicknameStatus === 'available' &&
                        displayName.toLowerCase() !== user.displayName.toLowerCase()
                      ? 'border-[#14f195]'
                      : ''
                  }`}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your display name"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {nicknameLoading ? (
                    <Loader2 className="w-4 h-4 text-[#0b0b0c]/45 animate-spin" />
                  ) : nicknameStatus === 'available' &&
                    displayName.toLowerCase() !== user.displayName.toLowerCase() ? (
                    <Check className="w-4 h-4 text-[#14f195]" />
                  ) : nicknameStatus === 'taken' ? (
                    <AlertCircle className="w-4 h-4 text-[#ff6a6a]" />
                  ) : null}
                </div>
              </div>
              {nicknameStatus === 'taken' && (
                <div className="mt-2 space-y-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#ff6a6a]">
                    Taken
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setDisplayName(s)}
                        className="px-3 min-h-9 rounded-full bg-white border-[1.5px] border-[#0b0b0c] text-[10px] font-bold hover:bg-[#0b0b0c] hover:text-white transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <FieldLabel>Company</FieldLabel>
              <input
                type="text"
                className={`${inputBase} px-4`}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Your company"
              />
            </div>

            <div className="sticker-soft bg-[#f3ecfc] p-3 flex items-center gap-3">
              <Mail className="w-4 h-4 text-[#0b0b0c]/55 shrink-0" />
              <div>
                <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
                  Email
                </p>
                <p className="text-sm font-bold text-[#0b0b0c] break-all">{user.email || 'Not connected'}</p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <h3 className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
              Platform data
            </h3>

            <div className="sticker-soft bg-[#f3ecfc] p-3 flex items-center gap-3">
              <Shield className="w-4 h-4 text-[#0b0b0c]/55 shrink-0" />
              <div>
                <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
                  Auth provider
                </p>
                <p className="text-sm font-bold text-[#0b0b0c] capitalize">{user.authProvider}</p>
              </div>
            </div>

            {user.authProvider === 'twitter' && (
              <div className="sticker-soft bg-[#f3ecfc] p-3 flex items-center gap-3">
                <Twitter className="w-4 h-4 text-[#9945ff] shrink-0" />
                <div>
                  <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
                    Twitter handle
                  </p>
                  <p className="text-sm font-bold text-[#9945ff]">@{user.twitterHandle}</p>
                </div>
              </div>
            )}

            <div className="sticker-soft bg-[#f3ecfc] p-3 flex items-center gap-3">
              <CalendarIcon className="w-4 h-4 text-[#0b0b0c]/55 shrink-0" />
              <div>
                <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
                  Member since
                </p>
                <p className="text-sm font-bold text-[#0b0b0c]">{memberSince}</p>
              </div>
            </div>

            <div className="sticker-soft bg-[#f3ecfc] p-3 flex items-center gap-3">
              <Building2 className="w-4 h-4 text-[#0b0b0c]/55 shrink-0" />
              <div>
                <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
                  Role
                </p>
                <p className="text-sm font-bold text-[#0b0b0c] capitalize">{user.role}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-[#0b0b0c]/15 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            {status && (
              <div
                className={`inline-flex items-center gap-2 ${
                  status.type === 'success' ? 'text-[#14f195]' : 'text-[#ff6a6a]'
                }`}
              >
                {status.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                <p className="text-xs font-bold">{status.message}</p>
              </div>
            )}
          </div>
          <button
            disabled={saving || nicknameStatus === 'taken' || nicknameLoading || !dirty}
            onClick={handleSave}
            className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            {saving ? (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="sticker bg-[#ff6a6a] text-white p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 sticker-soft bg-white flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-[#ff6a6a]" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-xl sm:text-2xl uppercase tracking-tight mb-1">Danger zone</h3>
            <p className="text-sm text-white/85 mb-4 leading-relaxed">
              Deleting your account is permanent. Profile data will be wiped, submitted events will be anonymized.
            </p>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-white text-[#ff6a6a] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
            >
              Delete my account
            </button>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center safe-overlay bg-[#0b0b0c]/40">
          <div className="sticker bg-[#e9dffa] max-w-lg w-full p-4 sm:p-6 mobile-sheet overflow-y-auto custom-scrollbar">
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sticker-soft bg-[#ff6a6a] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-xl sm:text-2xl uppercase tracking-tight text-[#0b0b0c]">
                  Delete account
                </h3>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="w-11 h-11 sm:w-9 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="sticker-soft bg-[#ffd24a] p-3 mb-4">
              <p className="text-sm text-[#0b0b0c] leading-relaxed">
                This action is permanent and cannot be undone. All your profile information will be deleted. Your submitted events will remain on the calendar but will be anonymized.
              </p>
            </div>

            <div className="mb-4">
              <FieldLabel>
                Type <span className="text-[#ff6a6a]">DELETE</span> to confirm
              </FieldLabel>
              <input
                type="text"
                className={`${inputBase} px-4`}
                placeholder="Type DELETE"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                disabled={deleting}
              />
            </div>

            {deleteError && (
              <div className="sticker-soft bg-[#ff6a6a] text-white p-3 mb-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-sm font-medium">{deleteError}</p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <button
                disabled={deleteConfirmation !== 'DELETE' || deleting}
                onClick={handleDeleteAccount}
                className="w-full h-12 rounded-full bg-[#ff6a6a] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Permanently delete'
                )}
              </button>
              <button
                disabled={deleting}
                onClick={() => setShowDeleteModal(false)}
                className="w-full h-11 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
